import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CustomerAccount, CustomerPasskeyCredential, CustomerProfile } from '../types'
import { useShopI18n } from '../../i18n'
import {
  buildCustomerAreaNavItems,
  CustomerAreaLayoutShell,
} from './customer-area'
import { CustomerProfileAccountInfoCard } from './customer-profile/CustomerProfileAccountInfoCard'
import { CustomerProfileFormCard } from './customer-profile/CustomerProfileFormCard'
import {
  fetchCustomerPasskeys,
  removeCustomerPasskey,
  requestCustomerPasskeyRegisterOptions,
  verifyCustomerPasskeyRegistration,
} from '../customerAuthApi'
import { createPasskeyRegistrationResponse, isPasskeyRegistrationSupported } from '../passkeyWebauthn'
import { PasskeySettingsPanel } from './customer-profile/PasskeySettingsPanel'

type CustomerProfilePageProps = {
  account: CustomerAccount
  profile: CustomerProfile
  onUpdateProfile: (payload: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    taxCode?: string
    residentialAddress?: string
  }) => Promise<boolean>
  onNavigate: (targetPath: string) => void
}

export const CustomerProfilePage = ({
  account,
  profile,
  onUpdateProfile,
  onNavigate,
}: CustomerProfilePageProps) => {
  const { language, t, toLocalizedPath } = useShopI18n()
  const [firstName, setFirstName] = useState(profile.firstName || '')
  const [lastName, setLastName] = useState(profile.lastName || '')
  const [email, setEmail] = useState(profile.email || '')
  const [phone, setPhone] = useState(profile.phone || '')
  const [taxCode, setTaxCode] = useState(profile.taxCode || '')
  const [residentialAddress, setResidentialAddress] = useState(profile.residentialAddress || '')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [passkeys, setPasskeys] = useState<CustomerPasskeyCredential[]>([])
  const [passkeysLoading, setPasskeysLoading] = useState(false)
  const [passkeysError, setPasskeysError] = useState('')
  const [passkeysSuccess, setPasskeysSuccess] = useState('')
  const [removingPasskeyId, setRemovingPasskeyId] = useState<string | null>(null)
  const twoFactorEnabled = account.twoFactorEnabled === true || account.mfaEnabled === true

  useEffect(() => {
    setFirstName(profile.firstName || '')
    setLastName(profile.lastName || '')
    setEmail(profile.email || '')
    setPhone(profile.phone || '')
    setTaxCode(profile.taxCode || '')
    setResidentialAddress(profile.residentialAddress || '')
  }, [profile])

  const hasChanges = useMemo(
    () =>
      firstName.trim() !== (profile.firstName || '').trim() ||
      lastName.trim() !== (profile.lastName || '').trim() ||
      email.trim() !== (profile.email || '').trim() ||
      phone.trim() !== (profile.phone || '').trim() ||
      taxCode.trim().toUpperCase() !== (profile.taxCode || '').trim().toUpperCase() ||
      residentialAddress.trim() !== (profile.residentialAddress || '').trim(),
    [email, firstName, lastName, phone, profile.email, profile.firstName, profile.lastName, profile.phone, profile.residentialAddress, profile.taxCode, residentialAddress, taxCode],
  )

  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
      }),
    [language, t, toLocalizedPath],
  )

  const handleSave = async () => {
    setSaving(true)
    setNotice('')
    try {
      const ok = await onUpdateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        taxCode: taxCode.trim().toUpperCase(),
        residentialAddress: residentialAddress.trim(),
      })
      setNotice(ok ? t('customer.profile.saved') : t('customer.profile.saveError'))
    } finally {
      setSaving(false)
    }
  }

  const loadPasskeys = useCallback(async () => {
    setPasskeysLoading(true)
    setPasskeysError('')
    try {
      const list = await fetchCustomerPasskeys()
      setPasskeys(list)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customer.profile.passkeys.error.load')
      setPasskeysError(message)
    } finally {
      setPasskeysLoading(false)
    }
  }, [t])

  const handleAddPasskey = async () => {
    if (!twoFactorEnabled) {
      setPasskeysError(t('customer.profile.passkeys.requiresTwoFactor'))
      setPasskeysSuccess('')
      return
    }

    setPasskeysLoading(true)
    setPasskeysError('')
    setPasskeysSuccess('')
    try {
      const payload = await requestCustomerPasskeyRegisterOptions()
      const options =
        payload.options && typeof payload.options === 'object'
          ? (payload.options as Record<string, unknown>)
          : payload
      const registrationResponse = await createPasskeyRegistrationResponse(options)
      await verifyCustomerPasskeyRegistration({ response: registrationResponse })
      const list = await fetchCustomerPasskeys()
      setPasskeys(list)
      setPasskeysSuccess(t('customer.profile.passkeys.success.added'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customer.profile.passkeys.error.add')
      setPasskeysError(message)
    } finally {
      setPasskeysLoading(false)
    }
  }

  const handleRemovePasskey = async (passkeyId: string) => {
    setRemovingPasskeyId(passkeyId)
    setPasskeysError('')
    setPasskeysSuccess('')
    try {
      await removeCustomerPasskey(passkeyId)
      const list = await fetchCustomerPasskeys()
      setPasskeys(list)
      setPasskeysSuccess(t('customer.profile.passkeys.success.removed'))
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customer.profile.passkeys.error.remove')
      setPasskeysError(message)
    } finally {
      setRemovingPasskeyId(null)
    }
  }

  useEffect(() => {
    void loadPasskeys()
  }, [loadPasskeys])

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.profile.title')}
      subtitle={t('customer.profile.subtitle')}
      navItems={navItems}
      activeNavId="profile"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'go-addresses',
          label: t('customer.nav.addresses'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard/addresses')),
          variant: 'secondary',
        },
      ]}
    >
      <CustomerProfileFormCard
        formState={{
          firstName,
          lastName,
          email,
          phone,
          taxCode,
          residentialAddress,
        }}
        saving={saving}
        hasChanges={hasChanges}
        notice={notice}
        t={t}
        onChangeField={(field, value) => {
          if (field === 'firstName') setFirstName(value)
          if (field === 'lastName') setLastName(value)
          if (field === 'email') setEmail(value)
          if (field === 'phone') setPhone(value)
          if (field === 'taxCode') setTaxCode(value)
          if (field === 'residentialAddress') setResidentialAddress(value)
        }}
        onSave={() => {
          void handleSave()
        }}
      />

      <CustomerProfileAccountInfoCard account={account} t={t} />
      <PasskeySettingsPanel
        t={t}
        passkeys={passkeys}
        loading={passkeysLoading}
        error={passkeysError}
        success={passkeysSuccess}
        removingId={removingPasskeyId}
        supported={isPasskeyRegistrationSupported()}
        twoFactorEnabled={twoFactorEnabled}
        onAddPasskey={() => {
          void handleAddPasskey()
        }}
        onRemovePasskey={(passkeyId) => {
          void handleRemovePasskey(passkeyId)
        }}
      />
    </CustomerAreaLayoutShell>
  )
}
